import React from 'react';
import { BoardMember } from '../types';

// Parse mentions from text (format: @username)
export const parseMentions = (text: string): string[] => {
  const mentionRegex = /@(\w+)/g;
  const mentions: string[] = [];
  let match;
  
  while ((match = mentionRegex.exec(text)) !== null) {
    mentions.push(match[1]);
  }
  
  return mentions;
};

// Convert mentions to user IDs
export const getMentionedUserIds = (text: string, boardMembers: BoardMember[]): string[] => {
  const mentions = parseMentions(text);
  const mentionedUserIds: string[] = [];
  
  mentions.forEach(mention => {
    const member = boardMembers.find(m => 
      m.name?.toLowerCase().includes(mention.toLowerCase()) ||
      m.email.toLowerCase().includes(mention.toLowerCase())
    );
    
    if (member) {
      mentionedUserIds.push(member.userId);
    }
  });
  
  return mentionedUserIds;
};

// Highlight mentions in text
export const highlightMentions = (text: string, boardMembers: BoardMember[]): React.ReactNode[] => {
  const parts: React.ReactNode[] = [];
  const mentionRegex = /@(\w+)/g;
  let lastIndex = 0;
  let match;
  
  while ((match = mentionRegex.exec(text)) !== null) {
    // Add text before the mention
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    
    // Check if the mention matches a board member
    const mention = match[1];
    const member = boardMembers.find(m => 
      m.name?.toLowerCase().includes(mention.toLowerCase()) ||
      m.email.toLowerCase().includes(mention.toLowerCase())
    );
    
    if (member) {
      // Valid mention - highlight it
      parts.push(
        <span key={match.index} className="bg-blue-100 text-blue-700 px-1 rounded font-medium">
          @{mention}
        </span>
      );
    } else {
      // Invalid mention - show as regular text
      parts.push(`@${mention}`);
    }
    
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  
  return parts;
};

// Get mention suggestions based on input
export const getMentionSuggestions = (input: string, boardMembers: BoardMember[]): BoardMember[] => {
  if (!input.startsWith('@')) return [];
  
  const query = input.slice(1).toLowerCase();
  if (!query) return boardMembers.slice(0, 5);
  
  return boardMembers
    .filter(member => 
      member.name?.toLowerCase().includes(query) ||
      member.email.toLowerCase().includes(query)
    )
    .slice(0, 5);
};
